<template>
  <div>
    <!-- 筛选条件区域 -->
    <canvas-glass-card title="筛选条件" variant="light" :hoverable="false">
      <a-form :model="queryForm" label-align="left">
        <a-grid :cols="4" :colGap="26" :rowGap="0" :collapsed="collapsed" :collapsed-rows="1">
          <a-grid-item>
            <a-form-item label="类型编码" label-col-flex="60px">
              <a-input v-model="queryForm.typeCode" placeholder="请输入类型编码" class="input" allow-clear/>
            </a-form-item>
          </a-grid-item>
          <a-grid-item>
            <a-form-item label="类型名称" label-col-flex="60px">
              <a-input v-model="queryForm.typeName" placeholder="请输入类型名称" class="input" allow-clear/>
            </a-form-item>
          </a-grid-item>
          <a-grid-item>
            <a-form-item label="状态" label-col-flex="60px">
              <a-select v-model="queryForm.status" placeholder="请选择状态">
                <a-option value="">全部</a-option>
                <a-option :value="true">启用</a-option>
                <a-option :value="false">停用</a-option>
              </a-select>
            </a-form-item>
          </a-grid-item>
          <a-grid-item suffix="suffix">
            <a-space>
              <a-button type="primary" @click="handleQuery" :loading="loading">
                <template #icon>
                  <icon-search style="stroke: #fff"/>
                </template>
                搜索
              </a-button>
              <a-button type="primary" status="warning" @click="handleReset">重置</a-button>
              <a-button type="text" @click="()=>{collapsed = !collapsed}">
                <template #icon>
                  <icon-down v-if="collapsed"/>
                  <icon-up v-else/>
                </template>
                {{ collapsed ? '展开' : '收起' }}
              </a-button>
            </a-space>
          </a-grid-item>
        </a-grid>
      </a-form>
    </canvas-glass-card>

    <a-space/>

    <!-- 字典类型列表表格 -->
    <canvas-glass-card variant="primary" :hoverable="false" class="table-card">
      <template #header>
        <a-row>
          <a-col flex="200px">
            <span class="glass-card__title">数据字典管理</span>
          </a-col>
          <a-col flex="auto"/>
          <a-col flex="120px">
            <a-row flex="120px" justify="space-between" :wrap="false">
              <a-button type="primary" @click="addDictType()">
                <template #icon>
                  <icon-plus style="stroke: #fff"/>
                </template>
                新增
              </a-button>
              <a-tooltip content="刷新">
                <a-button type="text" @click="fetchTableData">
                  <template #icon>
                    <icon-refresh/>
                  </template>
                </a-button>
              </a-tooltip>
            </a-row>
          </a-col>
        </a-row>
        {{ useSysDictDataStore().projectServiceTaskCategoryMap }}
      </template>

      <!-- 字典类型列表表格 -->
      <a-table
          row-key="id"
          :columns="tableColumns"
          :data="pagination.records"
          :pagination="false"
          :loading="loading"
          column-resizable
          stripe
          :bordered="{cell:true}"
      >
        <template #status="{ record }">
          <a-tag :color="record.status ? 'green' : 'red'">
            {{ record.status ? '启用' : '停用' }}
          </a-tag>
        </template>

        <template #createTime="{ record }">
          {{ formatDate(record.createTime) }}
        </template>

        <template #updateTime="{ record }">
          {{ formatDate(record.updateTime) }}
        </template>

        <template #action="{ record }">
          <a-space>
            <a-button type="text" size="mini" status="normal" @click="showDictDataBefore(record)"
                      class="button-table-option">
              <template #icon>
                <icon-list />
              </template>
              详情
            </a-button>
            <a-button type="text" size="mini" status="success" @click="editDictType(record)"
                      class="button-table-option">
              <template #icon>
                <icon-edit />
              </template>
              编辑
            </a-button>
            <a-button type="text" size="mini" status="warning" @click="deleteDictTypeBefore(record)"
                      class="button-table-option">
              <template #icon>
                <icon-delete />
              </template>
              删除
            </a-button>
          </a-space>
        </template>
      </a-table>
      <!-- 分页组件 -->
      <a-pagination
          style="justify-content: end;margin: 10px;"
          v-model:current="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-size-options="[10, 20, 50, 100]"
          show-total
          show-jumper
          show-page-size
          @change="handlePageChange"
          @page-size-change="handlePageSizeChange"
      />
    </canvas-glass-card>

    <!-- 字典数据详情抽屉 -->
    <dict-data-detail-drawer
        v-model="isShowDictDataDrawer"
        :dict-type="currentDictType"
    />

    <!-- 新增/修改字典类型模态框 -->
    <edit-modal 
        v-model="isShowEditModal" 
        :dict-type-id="currentEditDictTypeId"
        @success="handleEditSuccess"
    />
    
    <!-- 字典类型删除模态框 -->
    <a-modal
        v-model:visible="isShowDeleteDictTypeModal"
        :on-before-ok="delDictType"
        ok-text="删除"
        @cancel="isShowDeleteDictTypeModal = false"
        draggable
    >
      <template #title>
        是否删除
      </template>
      <div>确定要删除[<strong>{{ dictTypeInfoCopy.typeName }}</strong>]吗？</div>
    </a-modal>
  </div>
</template>

<script setup>
import {ref, onMounted} from 'vue'
import { 
  getSysDictTypePage, 
  deleteSysDictType
} from '@/apis/system-data'
import CanvasGlassCard from '@/components/CanvasGlassCard.vue'
import EditModal from '@/components/System/Dictionary/_Edit.vue'
import DictDataDetailDrawer from '@/components/System/Dictionary/DictDataDetailDrawer.vue'
import {
  IconSearch,
  IconDown,
  IconUp,
  IconPlus,
  IconRefresh,
  IconDelete,
  IconEdit,
  IconList
} from '@arco-design/web-vue/es/icon'
import {Message} from "@arco-design/web-vue"
import {timestampNumber2dateString} from "@/utils/date-util";
import {useSysDictDataStore} from "../../../store/system-data";

// 条件查询表单折叠
const collapsed = ref(true)

// 是否显示编辑模态框
const isShowEditModal = ref(false)
// 是否显示字典数据抽屉
const isShowDictDataDrawer = ref(false)
// 是否显示删除字典类型模态框
const isShowDeleteDictTypeModal = ref(false)
// 字典类型信息副本
const dictTypeInfoCopy = ref({})
// 当前编辑的字典类型ID
const currentEditDictTypeId = ref(null)
// 当前字典类型
const currentDictType = ref({})

// 筛选条件
const queryForm = ref({
  typeCode: '',
  typeName: '',
  status: ''
})

// 字典类型列表数据
let pagination = ref({
  records: [],
  current: 1,
  size: 10,
  pages: 0,
  total: 0
})

// 加载状态
const loading = ref(false)

// 表格列定义
const tableColumns = ref([
  {title: 'ID', dataIndex: 'id', width: 80},
  {title: '类型名称', dataIndex: 'typeName', width: 200, ellipsis: true, tooltip: true},
  {title: '类型编码', dataIndex: 'typeCode', width: 180, ellipsis: true, tooltip: true},
  {title: '状态', dataIndex: 'status', width: 80, slotName: 'status'},
  {title: '备注', dataIndex: 'remark', ellipsis: true, tooltip: true},
  {title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'createTime'},
  {title: '更新时间', dataIndex: 'updateTime', width: 160, slotName: 'updateTime'},
  {title: '操作', dataIndex: 'action', width: 180, slotName: 'action', align: 'center', fixed: 'right'}
])

// 获取字典类型列表
const fetchTableData = async () => {
  loading.value = true

  getSysDictTypePage({
    ...queryForm.value,
    current: pagination.value.current,
    size: pagination.value.size
  }).then(response => {
    loading.value = false
    if (response.code === 0) {
      pagination.value = response.data
    } else {
      Message.error(response.message)
      console.error('获取字典类型列表失败:', response)
    }
  }).catch(error => {
    loading.value = false
    Message.error(error.message || '获取字典类型列表失败')
    console.error('获取字典类型列表失败:', error)
  })
}

// 新增字典类型
const handleQuery = () => {
  pagination.current = 1
  fetchTableData()
}

// 重置筛选条件
const handleReset = () => {
  Object.keys(queryForm.value).forEach(key => {
    queryForm.value[key] instanceof Array ? queryForm.value[key].length = 0 : queryForm.value[key] = ''
  })
  fetchTableData()
}

// 处理页码变化
const handlePageChange = (page) => {
  fetchTableData()
}

// 处理每页数量变化
const handlePageSizeChange = (size) => {
  pagination.current = 1
  fetchTableData()
}

// 新增字典类型
const addDictType = () => {
  currentEditDictTypeId.value = null
  isShowEditModal.value = true
}

// 编辑字典类型
const editDictType = (dictType) => {
  currentEditDictTypeId.value = dictType.id
  isShowEditModal.value = true
}

// 编辑成功回调
const handleEditSuccess = () => {
  fetchTableData()
}

// 显示字典数据详情
const showDictDataBefore = (dictType) => {
  currentDictType.value = dictType
  isShowDictDataDrawer.value = true
}

// 删除字典类型前操作
const deleteDictTypeBefore = (dictType) => {
  dictTypeInfoCopy.value = dictType
  isShowDeleteDictTypeModal.value = true
}

// 删除字典类型
const delDictType = async () => {
  try {
    const response = await deleteSysDictType(dictTypeInfoCopy.value.id)
    if (response.code === 0) {
      Message.success('删除成功')
      fetchTableData()
      return true
    } else {
      Message.error(response.message || '删除失败')
      return false
    }
  } catch (error) {
    Message.error(error.message || '删除失败')
    console.error('删除字典类型失败:', error)
    return false
  }
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '—'
  // return dateString.substring(0, 16).replace('T', ' ');
  return timestampNumber2dateString(dateString)
}

// 组件挂载时获取数据
onMounted(() => {
  fetchTableData()
})
</script>

<style scoped>
.table-card {
  margin-bottom: 16px;
}

.button-table-option {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}

.glass-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

.input {
  width: 100%;
}
</style>